/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { toggleHasManyValue } from 'uplisting-frontend/utils/toggle-value';
import { searchOptions } from 'uplisting-frontend/utils/search';
import PropertyModel from 'uplisting-frontend/models/property';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';
import { type BulkStayStrategyChangeset } from 'uplisting-frontend/components/ui/stay-strategy/v1';

export interface PropertySelection {
  properties: ModelRegistry['property'][];
  multiUnits: ModelRegistry['multi-unit'][];
}

type Record =
  | GenericChangeset<ModelRegistry['invitation']>
  | BulkStayStrategyChangeset
  | ModelRegistry['teammate']
  | ModelRegistry['report']
  | PropertySelection;

type RecordWithMultiUnits =
  | GenericChangeset<ModelRegistry['invitation']>
  | ModelRegistry['teammate']
  | PropertySelection;

interface IArgs {
  isCollapsed?: boolean;
  record: Record;
  showMultiUnits?: boolean;
}

interface PropertySelectorSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

type Option = ModelRegistry['property'] | ModelRegistry['multi-unit'];

function removeIfIncluded(options: Option[], option: Option): void {
  if (options.includes(option)) {
    options.splice(options.indexOf(option), 1);
  }
}

function pushUnlessIncluded(options: Option[], option: Option): void {
  if (!options.includes(option)) {
    options.push(option);
  }
}

function propertiesAndMultiUnitsFor(
  properties: ModelRegistry['property'][],
): Option[] {
  return properties
    .map((property) => [property, ...property.multiUnits.slice()])
    .flat();
}

const isProperty = (option: Option): boolean => option instanceof PropertyModel;

const isMultiUnit = (option: Option): boolean =>
  option instanceof MultiUnitModel;

export default class UiPropertySelectorComponent extends Component<PropertySelectorSignature> {
  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @tracked inputValue = '';

  @cached
  get record(): Record {
    return this.args.record;
  }

  @cached
  get showMultiUnits(): boolean {
    return this.args.showMultiUnits ?? true;
  }

  @cached
  get isCollapsed(): boolean {
    return this.args.isCollapsed ?? false;
  }

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get options(): Option[] {
    // For performance there is no need to compute options if that section is collapsed
    if (this.isCollapsed) {
      return [];
    }

    if (this.showMultiUnits) {
      return propertiesAndMultiUnitsFor(this.properties);
    }

    return this.properties;
  }

  @cached
  get selectedOptions(): Option[] {
    const { record } = this;

    if (!record) {
      return [];
    }

    if (this.showMultiUnits) {
      return [
        ...record.properties,
        ...(record as RecordWithMultiUnits).multiUnits,
        ...propertiesAndMultiUnitsFor(record.properties),
      ];
    }

    return record.properties;
  }

  @cached
  get filteredOptions(): Option[] {
    return searchOptions<Option>(this.options, this.inputValue, 'searchName');
  }

  @cached
  get isAllSelected(): boolean {
    const { record } = this;

    if (!record) {
      return false;
    }

    const { showMultiUnits, filteredOptions } = this;

    if (!filteredOptions.length) {
      return false;
    }

    const { properties } = record;

    return filteredOptions.every((option) => {
      if (isProperty(option)) {
        return properties.includes(option as ModelRegistry['property']);
      }

      if (showMultiUnits) {
        return (record as RecordWithMultiUnits).multiUnits.includes(
          option as ModelRegistry['multi-unit'],
        );
      }

      return true;
    });
  }

  @action
  handleInputSearch(value: string): void {
    this.inputValue = value;
  }

  @action
  handelSelectAllClick(): void {
    const { record, showMultiUnits, filteredOptions } = this;

    const filteredProperties = filteredOptions.filter(isProperty);
    const filteredMultiUnits = filteredOptions.filter(isMultiUnit);

    if (this.isAllSelected) {
      filteredProperties.forEach((property) =>
        removeIfIncluded(record.properties, property),
      );

      if (showMultiUnits) {
        filteredMultiUnits.forEach((multiUnit) =>
          removeIfIncluded(
            (record as RecordWithMultiUnits).multiUnits,
            multiUnit,
          ),
        );
      }
    } else {
      filteredProperties.forEach((option) =>
        pushUnlessIncluded(record.properties, option),
      );

      if (showMultiUnits) {
        filteredMultiUnits.forEach((option) =>
          pushUnlessIncluded(
            (record as RecordWithMultiUnits).multiUnits,
            option,
          ),
        );
      }
    }
  }

  @action
  handleOptionClick(option: Option): void {
    const { properties } = this.record;

    if (isProperty(option)) {
      toggleHasManyValue(this.record, 'properties', option);

      if (!this.showMultiUnits) {
        return;
      }

      const isIncluded = properties.includes(
        option as ModelRegistry['property'],
      );

      (option as ModelRegistry['property']).multiUnits.forEach((multiUnit) => {
        if (isIncluded) {
          pushUnlessIncluded(
            (this.record as RecordWithMultiUnits).multiUnits,
            multiUnit,
          );
        } else {
          removeIfIncluded(
            (this.record as RecordWithMultiUnits).multiUnits,
            multiUnit,
          );
        }
      });
    } else {
      const record = this.record as RecordWithMultiUnits;

      toggleHasManyValue(record, 'multiUnits', option);

      const isIncluded = record.multiUnits.includes(option as MultiUnitModel);

      const { property } = option as MultiUnitModel;

      if (isIncluded) {
        const isAllMultiUnitsSelected = property.multiUnits.every((multiUnit) =>
          record.multiUnits.includes(multiUnit),
        );

        if (!isAllMultiUnitsSelected) {
          return;
        }

        pushUnlessIncluded(properties, property);
      } else {
        removeIfIncluded(properties, property);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Property::Selector': typeof UiPropertySelectorComponent;
  }
}
